/**
 * AsyncButton — wrapper de botao que combina automaticamente:
 *
 *   - Spinner inline durante execucao (MiniSpinner)
 *   - disabled = true durante run
 *   - toast.success(successMessage) ao resolver (se nao silentSuccess)
 *   - toast.error(errorMessage || err.message) ao rejeitar (se nao silentError)
 *   - Re-throw do erro QUANDO NAO silentError (caller pode tratar tambem).
 *     Com silentError o erro e engolido (caller ja trata inline) para nao
 *     virar unhandled rejection / overlay de erro do CRA em dev.
 *
 * Substitui o padrao manual:
 *
 *     const [salvando, setSalvando] = useState(false);
 *     const [error, setError] = useState(null);
 *     const handle = async () => {
 *       setSalvando(true);
 *       try { await acao(); ...
 *       } catch (err) { setError(err.message); }
 *       finally { setSalvando(false); }
 *     };
 *     <PrimaryButton onClick={handle} disabled={salvando}>Salvar</PrimaryButton>
 *
 * Por:
 *
 *     <AsyncButton onClick={acao} successMessage="Salvo">Salvar</AsyncButton>
 *
 * Para casos onde a tela trata erro inline (ex.: form com campos),
 * passe `silentError` — auto-toast nao dispara e voce trata no catch
 * com try/catch wrapping o `await onClick(e)`.
 *
 * Variantes: 'primary' (default), 'secondary', 'danger', 'icon'.
 *
 * IMPORTANTE: o onClick deve ser uma funcao que retorna Promise (async).
 * Se nao retornar, await resolve imediato (sem spinner/toast) — comportamento
 * identico ao Button normal. Retro-compativel.
 */
import React, { useState } from "react";

import { useToast } from "../../context/ToastContext";
import { MiniSpinner } from "../LoadingMarcenaria";
import {
  DangerButton,
  IconButton,
  PrimaryButton,
  SecondaryButton,
} from "../../pages/Orcamentos/styles";

const VARIANTS = {
  primary: PrimaryButton,
  secondary: SecondaryButton,
  danger: DangerButton,
  icon: IconButton,
};

function AsyncButton({
  onClick,
  variant = "primary",
  successMessage,
  errorMessage,
  silentSuccess = false,
  silentError = false,
  disabled = false,
  children,
  // Props passadas ao botao subjacente (style, title, etc)
  ...rest
}) {
  const [running, setRunning] = useState(false);
  const toast = useToast();
  const Btn = VARIANTS[variant] || PrimaryButton;

  const handle = async (e) => {
    if (running || disabled) return;
    setRunning(true);
    try {
      const result = await onClick(e);
      if (successMessage && !silentSuccess) {
        toast.success(successMessage);
      }
      return result;
    } catch (err) {
      if (!silentError) {
        // Auto-toast + re-throw para o caller poder tratar tambem se quiser.
        toast.error(errorMessage || err?.message || "Erro na operação.");
        throw err;
      }
      // silentError: o caller trata o erro inline (ex.: aviso no proprio
      // form/dialog). Engolimos aqui para nao virar unhandled rejection
      // (que dispara o overlay de erro do CRA em dev). O spinner para no
      // finally; silentSuccess evita toast falso de sucesso.
      return undefined;
    } finally {
      setRunning(false);
    }
  };

  return (
    <Btn
      type="button"
      onClick={handle}
      disabled={running || disabled}
      {...rest}
    >
      {running && (
        <MiniSpinner
          size={variant === "icon" ? 10 : 12}
          label="Processando"
        />
      )}
      {children}
    </Btn>
  );
}

export default AsyncButton;
